/**
 * Observations Offline Service
 *
 * Manages observations locally and syncs to server:
 *  - Save observation to IndexedDB (works offline)
 *  - Queue photos for upload
 *  - Sync observations + photos on reconnect
 */

import offlineDb from './offlineDb';
import api from './api';

const OBS_STORE = offlineDb.STORES.OBSERVATIONS;
const PHOTO_STORE = offlineDb.STORES.PHOTO_QUEUE;

/**
 * Generate a unique local ID for deduplication.
 */
export function generateLocalId() {
  return `obs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Save an observation locally.
 */
export async function saveObservationLocally(sessionId, observation) {
  const localId = observation.local_id || generateLocalId();
  const record = {
    local_id: localId,
    session_id: sessionId,
    note_text: observation.note_text,
    category: observation.category || 'general',
    severity: observation.severity || 'info',
    product_id: observation.product_id || null,
    product_name_snapshot: observation.product_name_snapshot || null,
    location_id: observation.location_id || null,
    location_name_snapshot: observation.location_name_snapshot || null,
    created_at: new Date().toISOString(),
    synced: false,
    server_id: null,
  };
  await offlineDb.put(OBS_STORE, record);
  return record;
}

/**
 * Get all observations for a session from IndexedDB.
 */
export async function getLocalObservations(sessionId) {
  const all = await offlineDb.getAll(OBS_STORE);
  return all.filter(o => o.session_id === sessionId);
}

/**
 * Queue a photo for upload (stores blob in IndexedDB).
 */
export async function queuePhotoUpload(observationLocalId, file, caption) {
  const localId = `photo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // Read file as ArrayBuffer for IndexedDB storage
  const arrayBuffer = await file.arrayBuffer();

  const record = {
    local_id: localId,
    observation_local_id: observationLocalId,
    file_name: file.name,
    mime_type: file.type || 'image/jpeg',
    file_size: file.size,
    blob: arrayBuffer,
    caption: caption || null,
    status: 'pending', // pending | uploading | uploaded | failed
    created_at: new Date().toISOString(),
  };
  await offlineDb.put(PHOTO_STORE, record);
  return record;
}

/**
 * Get queued photos for an observation.
 */
export async function getQueuedPhotos(observationLocalId) {
  const all = await offlineDb.getAll(PHOTO_STORE);
  return all.filter(p => p.observation_local_id === observationLocalId);
}

/**
 * Get all pending photos.
 */
export async function getAllPendingPhotos() {
  const all = await offlineDb.getAll(PHOTO_STORE);
  return all.filter(p => p.status === 'pending' || p.status === 'failed');
}

/**
 * Sync observations to server, then upload any queued photos.
 */
export async function syncObservations(sessionId) {
  const locals = await getLocalObservations(sessionId);
  const unsynced = locals.filter(o => !o.synced);

  if (unsynced.length === 0) {
    // Still try to upload pending photos for already-synced observations
    await _uploadPendingPhotos(locals);
    return { created: 0, skipped: 0, photos_uploaded: 0 };
  }

  const payload = {
    observations: unsynced.map(o => ({
      local_id: o.local_id,
      note_text: o.note_text,
      category: o.category,
      severity: o.severity,
      product_id: o.product_id,
      product_name_snapshot: o.product_name_snapshot,
      location_id: o.location_id,
      location_name_snapshot: o.location_name_snapshot,
      created_at: o.created_at,
    })),
  };

  const res = await api.post(`/count/sessions/${sessionId}/observations/sync`, payload);
  const result = res.data;
  const idMap = result.id_map || {};

  // Mark as synced and store server_id
  for (const o of unsynced) {
    o.synced = true;
    if (idMap[o.local_id]) {
      o.server_id = idMap[o.local_id];
    }
    await offlineDb.put(OBS_STORE, o);
  }

  // Upload queued photos using the id_map
  const allLocal = await getLocalObservations(sessionId);
  const photosUploaded = await _uploadPendingPhotos(allLocal);
  result.photos_uploaded = photosUploaded;

  return result;
}

/**
 * Upload pending photos for observations that have a server_id.
 */
async function _uploadPendingPhotos(observations) {
  let uploaded = 0;
  for (const obs of observations) {
    if (!obs.server_id) continue;
    const photos = await getQueuedPhotos(obs.local_id);
    const pending = photos.filter(p => p.status === 'pending' || p.status === 'failed');
    for (const photo of pending) {
      const result = await uploadQueuedPhoto(photo, obs.server_id);
      if (result.success) uploaded++;
    }
  }
  return uploaded;
}

/**
 * Upload a single queued photo to its server-side observation.
 * Requires the observation to have been synced first (needs server ID).
 */
export async function uploadQueuedPhoto(photoRecord, serverObservationId) {
  photoRecord.status = 'uploading';
  await offlineDb.put(PHOTO_STORE, photoRecord);

  try {
    const blob = new Blob([photoRecord.blob], { type: photoRecord.mime_type });
    const formData = new FormData();
    formData.append('file', blob, photoRecord.file_name);
    if (photoRecord.caption) {
      formData.append('caption', photoRecord.caption);
    }

    const res = await api.post(`/count/observations/${serverObservationId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    photoRecord.status = 'uploaded';
    photoRecord.server_photo = res.data.photo;
    await offlineDb.put(PHOTO_STORE, photoRecord);

    return { success: true, photo: res.data.photo };
  } catch (e) {
    photoRecord.status = 'failed';
    await offlineDb.put(PHOTO_STORE, photoRecord);
    return { success: false, error: e.message };
  }
}

/**
 * Remove a local observation.
 */
export async function removeLocalObservation(localId) {
  await offlineDb.remove(OBS_STORE, localId);
  // Also remove queued photos for this observation
  const photos = await getQueuedPhotos(localId);
  for (const p of photos) {
    await offlineDb.remove(PHOTO_STORE, p.local_id);
  }
}
